import { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Flex, Text, VStack, HStack, Spinner, Button, IconButton,
    useToast, Input, Badge, Tooltip, Avatar, Image,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
    useBreakpointValue, AlertDialog, AlertDialogOverlay, AlertDialogContent,
    AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
    useDisclosure, Wrap, WrapItem
} from "@chakra-ui/react";
import {
    Send, Plus, Trash2, MessageSquare, Bot, User, ArrowLeft,
    Sparkles, RefreshCw, AlertTriangle, Clock, Menu,
    ImagePlus, FileText, Mic, X, Paperclip, Download, FileAudio
} from "lucide-react";
import {
    getChatIAConversas, postChatIA, getChatIAConversa,
    postChatIAMensagem, deleteChatIA, getChatIASolicitacao
} from "../services/api";
import { baseUrlApi } from "../config";

const ALTURA_TOTAL = "calc(100vh - 130px)";

// Base URL do storage (remove /api do final)
const STORAGE_BASE = baseUrlApi.replace(/\/api$/, "");

const LIMITES = {
    imagensMax: 3,
    imagemTamanho: 10 * 1024 * 1024,
    pdfTamanho: 20 * 1024 * 1024,
    audioTamanho: 25 * 1024 * 1024,
    imagemTipos: ["image/jpeg", "image/png", "image/webp", "image/gif"],
    pdfTipos: ["application/pdf"],
    audioTipos: ["audio/mpeg", "audio/ogg", "audio/wav", "audio/mp4", "audio/x-m4a", "audio/webm", "audio/aac", "audio/flac", "audio/opus"],
};

const TIPOS_ERRO = {
    rate_limit: { titulo: "Limite de requisicoes atingido", descricao: "Aguarde cerca de 1 minuto e tente novamente.", podeRetentar: true },
    overloaded: { titulo: "API sobrecarregada", descricao: "Tente novamente em alguns segundos.", podeRetentar: true },
    timeout: { titulo: "Tempo esgotado", descricao: "A consulta passou de 10 minutos de pesquisa. Tente dividir a pergunta em partes menores.", podeRetentar: true },
    auth: { titulo: "Credenciais invalidas", descricao: "Contate o administrador.", podeRetentar: false },
    empty_response: { titulo: "Sem resposta", descricao: "A IA nao gerou texto. Tente reformular sua pergunta.", podeRetentar: true },
    max_iterations: { titulo: "Consulta muito complexa", descricao: "Tente simplificar a pergunta.", podeRetentar: true },
    em_andamento: { titulo: "Consulta em andamento", descricao: "Ja existe uma consulta sendo processada nesta conversa. Aguarde a resposta.", podeRetentar: false },
    job_failed: { titulo: "Falha no processamento", descricao: "O processamento falhou inesperadamente. Tente novamente.", podeRetentar: true },
    poll_falhou: { titulo: "Sem conexao com o servidor", descricao: "A consulta continua sendo processada no servidor. Reabra a conversa em instantes para ver a resposta.", podeRetentar: false },
    poll_timeout: { titulo: "Tempo limite de acompanhamento", descricao: "Reabra a conversa para verificar se a resposta chegou.", podeRetentar: false },
};

const mapearErro = (res) => {
    const tipo = res?.tipo;
    const cfg = TIPOS_ERRO[tipo];
    if (cfg) return { ...cfg, tipo };
    return { tipo: tipo || "unknown", titulo: "Erro ao enviar mensagem", descricao: res?.erro || "Erro desconhecido.", podeRetentar: true };
};

// ── Polling da solicitacao ──
// O backend processa a resposta em background (job de fila); aqui só se
// consulta o andamento. Falhas isoladas de rede NAO abortam a espera — esse é
// o ponto da arquitetura assincrona (buscas longas nao dependem de uma
// conexao aberta).
const POLL_INTERVALO_MS = 3000;
const POLL_MAX_MS = 18 * 60 * 1000; // seguranca local; o job marca erro bem antes (timeout de 16min)
const POLL_FALHAS_SEGUIDAS_MAX = 10; // ~30s de API fora do ar

const aguardarSolicitacao = async (solicitacaoId) => {
    const inicio = Date.now();
    let falhasSeguidas = 0;
    while (Date.now() - inicio < POLL_MAX_MS) {
        await new Promise(r => setTimeout(r, POLL_INTERVALO_MS));
        const res = await getChatIASolicitacao(solicitacaoId);
        if (!res || !res.status) {
            falhasSeguidas += 1;
            if (falhasSeguidas >= POLL_FALHAS_SEGUIDAS_MAX) {
                return { status: "erro", erro: "Nao foi possivel consultar o andamento da resposta.", tipo: "poll_falhou" };
            }
            continue;
        }
        falhasSeguidas = 0;
        if (res.status === "concluida" || res.status === "erro") return res;
    }
    return { status: "erro", erro: "A consulta passou do tempo limite de acompanhamento.", tipo: "poll_timeout" };
};

const formatarData = (data) => {
    if (!data) return "";
    const d = new Date(data);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);
    if (d.toDateString() === hoje.toDateString()) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    if (d.toDateString() === ontem.toDateString()) return "Ontem " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const formatarTamanho = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const resolverUrl = (url) => {
    if (!url) return "";
    // Se a URL veio com base do servidor (localhost ou outro), extrair apenas o path
    if (url.startsWith("http")) {
        try {
            const parsed = new URL(url);
            return STORAGE_BASE + parsed.pathname;
        } catch {
            return url;
        }
    }
    return STORAGE_BASE + url;
};

const ChatIA = () => {
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;

    const [conversas, setConversas] = useState([]);
    const [loadingConversas, setLoadingConversas] = useState(true);
    const [conversaAtiva, setConversaAtiva] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [loadingMensagens, setLoadingMensagens] = useState(false);
    const [inputMsg, setInputMsg] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [tempoEnvio, setTempoEnvio] = useState(0);
    const [sidebarAberta, setSidebarAberta] = useState(false);

    // Anexos
    const [imagens, setImagens] = useState([]);       // File[]
    const [pdf, setPdf] = useState(null);              // File | null
    const [audio, setAudio] = useState(null);          // File | null

    const { isOpen: deleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [conversaParaDeletar, setConversaParaDeletar] = useState(null);
    const cancelDeleteRef = useRef(null);

    const mensagensEndRef = useRef(null);
    const inputRef = useRef(null);
    const imagemInputRef = useRef(null);
    const pdfInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const dataFetchedRef = useRef(false);
    const timerRef = useRef(null);
    // Espelho da conversa ativa para os awaits longos do polling: o usuario
    // pode trocar de conversa enquanto a resposta processa, e o resultado nao
    // pode ser aplicado na conversa errada.
    const conversaAtivaRef = useRef(null);

    useEffect(() => { conversaAtivaRef.current = conversaAtiva; }, [conversaAtiva]);

    const temAnexos = imagens.length > 0 || pdf || audio;
    const podeEnviar = (inputMsg.trim() || temAnexos) && !enviando;

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        carregarConversas();
    }, []);

    useEffect(() => {
        if (enviando) {
            setTempoEnvio(0);
            timerRef.current = setInterval(() => setTempoEnvio(p => p + 1), 1000);
        } else if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [enviando]);

    const scrollParaFinal = useCallback(() => {
        setTimeout(() => { mensagensEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, 100);
    }, []);

    const limparAnexos = () => {
        setImagens([]);
        setPdf(null);
        setAudio(null);
        if (imagemInputRef.current) imagemInputRef.current.value = "";
        if (pdfInputRef.current) pdfInputRef.current.value = "";
        if (audioInputRef.current) audioInputRef.current.value = "";
    };

    // ── Handlers de arquivo ──
    const handleImagens = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const disponiveis = LIMITES.imagensMax - imagens.length;
        if (disponiveis <= 0) {
            toast({ title: `Maximo ${LIMITES.imagensMax} imagens`, status: "warning", duration: 3000 });
            return;
        }
        const validas = [];
        for (const f of files.slice(0, disponiveis)) {
            if (!LIMITES.imagemTipos.includes(f.type)) {
                toast({ title: `Formato nao suportado: ${f.name}`, description: "Use JPEG, PNG, WebP ou GIF", status: "warning", duration: 3000 });
                continue;
            }
            if (f.size > LIMITES.imagemTamanho) {
                toast({ title: `Imagem muito grande: ${f.name}`, description: "Maximo 10MB por imagem", status: "warning", duration: 3000 });
                continue;
            }
            validas.push(f);
        }
        if (validas.length) setImagens(prev => [...prev, ...validas]);
        e.target.value = "";
    };

    const handlePdf = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!LIMITES.pdfTipos.includes(f.type)) {
            toast({ title: "Formato invalido", description: "Envie um arquivo PDF", status: "warning", duration: 3000 });
            e.target.value = "";
            return;
        }
        if (f.size > LIMITES.pdfTamanho) {
            toast({ title: "PDF muito grande", description: "Maximo 20MB", status: "warning", duration: 3000 });
            e.target.value = "";
            return;
        }
        setPdf(f);
        e.target.value = "";
    };

    const handleAudio = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > LIMITES.audioTamanho) {
            toast({ title: "Audio muito grande", description: "Maximo 25MB", status: "warning", duration: 3000 });
            e.target.value = "";
            return;
        }
        setAudio(f);
        e.target.value = "";
    };

    const removerImagem = (idx) => setImagens(prev => prev.filter((_, i) => i !== idx));

    // ── API calls ──
    const carregarConversas = async () => {
        setLoadingConversas(true);
        const res = await getChatIAConversas(100);
        if (res && res.data) setConversas(res.data);
        setLoadingConversas(false);
    };

    const abrirConversa = async (conversa) => {
        setConversaAtiva(conversa);
        conversaAtivaRef.current = conversa;
        setMensagens([]);
        setLoadingMensagens(true);
        setSidebarAberta(false);
        limparAnexos();

        const res = await getChatIAConversa(conversa.id);
        if (res && res.mensagens) {
            setMensagens(res.mensagens);
            setTimeout(() => scrollParaFinal(), 150);
            // Consulta ficou processando no servidor (ex.: F5 no meio da
            // espera) — retoma o acompanhamento. Se ja ha um polling ativo
            // nesta sessao (enviando), ele mesmo entrega o resultado.
            if (res.solicitacao_pendente && !enviando) {
                retomarSolicitacao(conversa.id, res.solicitacao_pendente);
            }
        } else {
            toast({ title: "Erro ao carregar conversa", status: "error", duration: 3000 });
        }
        setLoadingMensagens(false);
        setTimeout(() => inputRef.current?.focus(), 200);
    };

    // Retoma o acompanhamento de uma solicitacao pendente no servidor.
    const retomarSolicitacao = async (chatId, pendente) => {
        setMensagens(prev => [...prev, {
            id: "pendente-" + pendente.id,
            role: "user",
            conteudo: pendente.mensagem || "[Anexo]",
            anexos: pendente.anexos || null,
            criado_em: null,
        }]);
        setEnviando(true);
        scrollParaFinal();

        const res = await aguardarSolicitacao(pendente.id);
        const aindaNoChat = conversaAtivaRef.current?.id === chatId;

        if (res && res.status === "concluida") {
            setConversas(prev => prev.map(c =>
                c.id === chatId ? { ...c, atualizado_em: new Date().toISOString() } : c
            ));
            if (aindaNoChat) {
                const conv = await getChatIAConversa(chatId);
                if (conv && conv.mensagens && conversaAtivaRef.current?.id === chatId) {
                    setMensagens(conv.mensagens);
                    scrollParaFinal();
                }
            }
        } else if (res && res.erro && aindaNoChat) {
            const info = mapearErro(res);
            setMensagens(prev => [...prev, { id: "error-" + Date.now(), role: "error", conteudo: info.descricao, titulo: info.titulo, podeRetentar: false }]);
            scrollParaFinal();
        }
        setEnviando(false);
    };

    const novaConversa = () => {
        setConversaAtiva(null);
        setMensagens([]);
        setInputMsg("");
        limparAnexos();
        setSidebarAberta(false);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const montarDescricaoAnexos = () => {
        const partes = [];
        if (imagens.length) partes.push(`${imagens.length} imagem(ns)`);
        if (pdf) partes.push(`PDF: ${pdf.name}`);
        if (audio) partes.push(`Audio: ${audio.name}`);
        return partes.length ? "\n[" + partes.join(", ") + "]" : "";
    };

    const enviarMensagem = async () => {
        const texto = inputMsg.trim();
        if ((!texto && !temAnexos) || enviando) return;

        const anexosEnvio = { imagens: imagens.length ? [...imagens] : undefined, pdf: pdf || undefined, audio: audio || undefined };
        const descAnexos = montarDescricaoAnexos();
        const conteudoExibicao = (texto || "") + descAnexos;

        // Monta previews para a msg temporaria
        const anexosTemp = [];
        imagens.forEach(f => anexosTemp.push({ tipo: "imagem", nome_original: f.name, _localUrl: URL.createObjectURL(f) }));
        if (pdf) anexosTemp.push({ tipo: "pdf", nome_original: pdf.name });
        if (audio) anexosTemp.push({ tipo: "audio", nome_original: audio.name, _localUrl: URL.createObjectURL(audio) });

        setEnviando(true);
        setInputMsg("");
        limparAnexos();

        if (!conversaAtiva) {
            const msgTemp = { id: "temp-user", role: "user", conteudo: conteudoExibicao, anexos: anexosTemp.length ? anexosTemp : null, criado_em: new Date().toISOString() };
            setMensagens([msgTemp]);
            scrollParaFinal();

            // Cria a solicitacao (rapido) e acompanha o processamento por polling
            let res = await postChatIA(texto || null, anexosEnvio);
            if (res && res.solicitacao_id) {
                res = await aguardarSolicitacao(res.solicitacao_id);
            }

            // Usuario pode ter aberto outra conversa durante a espera
            const aindaNaTelaNova = conversaAtivaRef.current === null;

            if (res && res.status === "concluida" && res.chat_id && res.resposta) {
                const novaConv = { id: res.chat_id, titulo: res.titulo, criado_em: res.criado_em, atualizado_em: res.criado_em };
                setConversas(prev => [novaConv, ...prev]);
                if (aindaNaTelaNova) {
                    setConversaAtiva(novaConv);
                    setMensagens([
                        { ...msgTemp, id: "user-1", anexos: res.anexos || msgTemp.anexos },
                        { id: "assistant-1", role: "assistant", conteudo: res.resposta, anexos: res.anexos_assistente || null, criado_em: res.criado_em }
                    ]);
                    scrollParaFinal();
                }
            } else if (res && res.erro) {
                if (aindaNaTelaNova) {
                    const info = mapearErro(res);
                    setMensagens(prev => [...prev, { id: "error", role: "error", conteudo: info.descricao, titulo: info.titulo, podeRetentar: info.podeRetentar }]);
                    setInputMsg(texto);
                    scrollParaFinal();
                }
            } else if (aindaNaTelaNova) {
                toast({ title: "Erro de conexao", description: "Verifique sua internet.", status: "error", duration: 4000 });
                setInputMsg(texto);
            }
        } else {
            const chatIdEnvio = conversaAtiva.id;
            const msgTemp = { id: "temp-user-" + Date.now(), role: "user", conteudo: conteudoExibicao, anexos: anexosTemp.length ? anexosTemp : null, criado_em: new Date().toISOString() };
            setMensagens(prev => [...prev, msgTemp]);
            scrollParaFinal();

            let res = await postChatIAMensagem(chatIdEnvio, texto || null, anexosEnvio);
            if (res && res.solicitacao_id) {
                res = await aguardarSolicitacao(res.solicitacao_id);
            }

            const aindaNoChat = conversaAtivaRef.current?.id === chatIdEnvio;

            if (res && res.status === "concluida" && res.resposta) {
                if (aindaNoChat) {
                    setMensagens(prev => {
                        const semTemp = prev.filter(m => m.id !== msgTemp.id);
                        return [
                            ...semTemp,
                            { id: "user-" + Date.now(), role: "user", conteudo: conteudoExibicao, anexos: res.anexos || msgTemp.anexos, criado_em: new Date().toISOString() },
                            { id: "assistant-" + Date.now(), role: "assistant", conteudo: res.resposta, anexos: res.anexos_assistente || null, criado_em: new Date().toISOString() }
                        ];
                    });
                    scrollParaFinal();
                }
                setConversas(prev => prev.map(c =>
                    c.id === chatIdEnvio ? { ...c, atualizado_em: new Date().toISOString() } : c
                ));
            } else if (res && res.erro) {
                if (aindaNoChat) {
                    const info = mapearErro(res);
                    setMensagens(prev => [...prev, { id: "error-" + Date.now(), role: "error", conteudo: info.descricao, titulo: info.titulo, podeRetentar: info.podeRetentar }]);
                    setInputMsg(texto);
                    scrollParaFinal();
                }
            } else if (aindaNoChat) {
                toast({ title: "Erro de conexao", status: "error", duration: 3000 });
                setInputMsg(texto);
            }
        }
        setEnviando(false);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const confirmarDelete = (conversa, e) => { e.stopPropagation(); setConversaParaDeletar(conversa); onDeleteOpen(); };

    const executarDelete = async () => {
        if (!conversaParaDeletar) return;
        const res = await deleteChatIA(conversaParaDeletar.id);
        if (res && res.success) {
            setConversas(prev => prev.filter(c => c.id !== conversaParaDeletar.id));
            if (conversaAtiva?.id === conversaParaDeletar.id) { setConversaAtiva(null); setMensagens([]); }
            toast({ title: "Conversa excluida", status: "info", duration: 2000 });
        } else {
            toast({ title: "Erro ao excluir", status: "error", duration: 3000 });
        }
        setConversaParaDeletar(null);
        onDeleteClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarMensagem(); }
    };

    const formatarTempo = (s) => {
        const min = Math.floor(s / 60);
        const sec = s % 60;
        return min > 0 ? `${min}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
    };

    const getMensagemLoading = () => {
        if (tempoEnvio < 8) return "Enviando para o assistente...";
        if (tempoEnvio < 20) return "Buscando produtos no catalogo...";
        if (tempoEnvio < 40) return "Pesquisando codigos equivalentes...";
        if (tempoEnvio < 70) return "Consultando catalogo de pecas originais...";
        if (tempoEnvio < 120) return "Verificando estoque e precos...";
        if (tempoEnvio < 180) return "Montando resposta...";
        if (tempoEnvio < 300) return "Consulta profunda em andamento...";
        return "Consulta longa — pode sair da pagina, a resposta fica salva na conversa.";
    };

    // ====== RENDER: Anexos de uma mensagem ======
    const renderAnexosMensagem = (anexos, isUser) => {
        if (!anexos || !anexos.length) return null;
        return (
            <VStack spacing={2} mt={2} align={isUser ? "flex-end" : "flex-start"} w="100%">
                {anexos.map((anx, i) => {
                    if (anx.tipo === "imagem") {
                        const src = anx._localUrl || resolverUrl(anx.url);
                        return (
                            <Box
                                key={i}
                                borderRadius="md"
                                overflow="hidden"
                                maxW="280px"
                                cursor="pointer"
                                onClick={() => src && window.open(src, "_blank")}
                            >
                                <Image
                                    src={src}
                                    alt={anx.nome_original || "Imagem"}
                                    maxH="200px"
                                    objectFit="cover"
                                    borderRadius="md"
                                />
                                {anx.nome_original && (
                                    <Text fontSize="10px" color={isUser ? "whiteAlpha.700" : "gray.400"} mt={0.5}>
                                        {anx.nome_original}
                                    </Text>
                                )}
                            </Box>
                        );
                    }
                    if (anx.tipo === "pdf") {
                        const href = anx.url ? resolverUrl(anx.url) : null;
                        return (
                            <HStack
                                key={i}
                                spacing={2}
                                bg={isUser ? "whiteAlpha.200" : "gray.50"}
                                px={3} py={2}
                                borderRadius="md"
                                cursor={href ? "pointer" : "default"}
                                onClick={() => href && window.open(href, "_blank")}
                                _hover={href ? { bg: isUser ? "whiteAlpha.300" : "gray.100" } : {}}
                            >
                                <FileText size={16} color={isUser ? "#fff" : "#DD6B20"} />
                                <Text fontSize="xs" fontWeight="500" color={isUser ? "white" : "gray.700"} noOfLines={1}>
                                    {anx.nome_original || "Documento PDF"}
                                </Text>
                                {href && <Download size={12} color={isUser ? "#fff" : "#A0AEC0"} />}
                            </HStack>
                        );
                    }
                    if (anx.tipo === "audio") {
                        const src = anx._localUrl || resolverUrl(anx.url);
                        return (
                            <VStack key={i} spacing={1} align="stretch" w="100%" maxW="280px">
                                <HStack spacing={2}>
                                    <FileAudio size={14} color={isUser ? "#fff" : "#DD6B20"} />
                                    <Text fontSize="xs" color={isUser ? "whiteAlpha.800" : "gray.500"} noOfLines={1}>
                                        {anx.nome_original || "Audio"}
                                    </Text>
                                </HStack>
                                {src && (
                                    <audio controls style={{ width: "100%", height: "32px" }} src={src}>
                                        Seu navegador nao suporta audio.
                                    </audio>
                                )}
                                {anx.transcricao && (
                                    <Box bg={isUser ? "whiteAlpha.200" : "orange.50"} px={2} py={1} borderRadius="md">
                                        <Text fontSize="10px" color={isUser ? "whiteAlpha.800" : "gray.600"} fontStyle="italic">
                                            Transcricao: {anx.transcricao}
                                        </Text>
                                    </Box>
                                )}
                            </VStack>
                        );
                    }
                    return null;
                })}
            </VStack>
        );
    };

    // ====== RENDER: Preview dos anexos selecionados (input area) ======
    const renderPreviewAnexos = () => {
        if (!temAnexos) return null;
        return (
            <Wrap spacing={2} mb={2} px={1}>
                {imagens.map((img, i) => (
                    <WrapItem key={"img-" + i}>
                        <Box position="relative" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
                            <Image
                                src={URL.createObjectURL(img)}
                                alt={img.name}
                                h="60px" w="60px"
                                objectFit="cover"
                            />
                            <IconButton
                                icon={<X size={10} />}
                                size="xs"
                                position="absolute"
                                top={0} right={0}
                                bg="blackAlpha.600"
                                color="white"
                                borderRadius="0 0 0 4px"
                                minW="16px" h="16px"
                                _hover={{ bg: "red.500" }}
                                onClick={() => removerImagem(i)}
                                aria-label="Remover"
                            />
                            <Text fontSize="8px" color="gray.500" textAlign="center" noOfLines={1} px={0.5}>
                                {formatarTamanho(img.size)}
                            </Text>
                        </Box>
                    </WrapItem>
                ))}
                {pdf && (
                    <WrapItem>
                        <HStack
                            spacing={1.5}
                            bg="red.50"
                            border="1px solid"
                            borderColor="red.200"
                            px={2} py={1.5}
                            borderRadius="md"
                        >
                            <FileText size={14} color="#E53E3E" />
                            <VStack spacing={0} align="start">
                                <Text fontSize="xs" fontWeight="500" color="gray.700" noOfLines={1} maxW="120px">
                                    {pdf.name}
                                </Text>
                                <Text fontSize="9px" color="gray.400">{formatarTamanho(pdf.size)}</Text>
                            </VStack>
                            <IconButton
                                icon={<X size={12} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: "red.500" }}
                                onClick={() => setPdf(null)}
                                aria-label="Remover PDF"
                                minW="20px" h="20px"
                            />
                        </HStack>
                    </WrapItem>
                )}
                {audio && (
                    <WrapItem>
                        <HStack
                            spacing={1.5}
                            bg="purple.50"
                            border="1px solid"
                            borderColor="purple.200"
                            px={2} py={1.5}
                            borderRadius="md"
                        >
                            <Mic size={14} color="#805AD5" />
                            <VStack spacing={0} align="start">
                                <Text fontSize="xs" fontWeight="500" color="gray.700" noOfLines={1} maxW="120px">
                                    {audio.name}
                                </Text>
                                <Text fontSize="9px" color="gray.400">{formatarTamanho(audio.size)}</Text>
                            </VStack>
                            <IconButton
                                icon={<X size={12} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: "red.500" }}
                                onClick={() => setAudio(null)}
                                aria-label="Remover audio"
                                minW="20px" h="20px"
                            />
                        </HStack>
                    </WrapItem>
                )}
            </Wrap>
        );
    };

    // ====== SIDEBAR DE CONVERSAS ======
    const renderSidebar = () => (
        <Flex direction="column" h="100%" overflow="hidden">
            <Box p={3} borderBottom="1px solid" borderColor="gray.100" flexShrink={0}>
                <Button w="100%" size="sm" colorScheme="orange" leftIcon={<Plus size={16} />} onClick={novaConversa} fontWeight="600">
                    Nova Conversa
                </Button>
            </Box>
            <Box flex={1} overflowY="auto">
                {loadingConversas ? (
                    <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                ) : conversas.length === 0 ? (
                    <VStack spacing={3} py={10} px={4}>
                        <MessageSquare size={36} color="#CBD5E0" />
                        <Text textAlign="center" color="gray.400" fontSize="sm">Nenhuma conversa ainda</Text>
                        <Text textAlign="center" color="gray.300" fontSize="xs">Clique em "Nova Conversa" para comecar</Text>
                    </VStack>
                ) : (
                    <VStack spacing={0} align="stretch">
                        {conversas.map((conv) => {
                            const ativo = conversaAtiva?.id === conv.id;
                            return (
                                <Box
                                    key={conv.id} px={3} py={2.5} cursor="pointer"
                                    bg={ativo ? "orange.50" : "white"}
                                    borderLeft="3px solid" borderLeftColor={ativo ? "orange.400" : "transparent"}
                                    _hover={{ bg: ativo ? "orange.50" : "gray.50" }}
                                    onClick={() => abrirConversa(conv)}
                                    borderBottom="1px solid" borderBottomColor="gray.50"
                                    role="group" transition="all 0.12s"
                                >
                                    <Flex align="start" gap={2}>
                                        <Box pt={0.5} flexShrink={0}>
                                            <MessageSquare size={14} color={ativo ? "#DD6B20" : "#A0AEC0"} />
                                        </Box>
                                        <Box flex={1} minW={0}>
                                            <Text fontWeight={ativo ? "600" : "500"} fontSize="xs" color={ativo ? "orange.700" : "gray.700"} noOfLines={2} lineHeight="1.4">
                                                {conv.titulo}
                                            </Text>
                                            <HStack mt={1} spacing={1}>
                                                <Clock size={10} color="#A0AEC0" />
                                                <Text fontSize="10px" color="gray.400">{formatarData(conv.atualizado_em || conv.criado_em)}</Text>
                                            </HStack>
                                        </Box>
                                        <Tooltip label="Excluir conversa" placement="left">
                                            <IconButton
                                                icon={<Trash2 size={13} />} size="xs" variant="ghost" color="gray.400"
                                                _hover={{ color: "red.500", bg: "red.50" }}
                                                opacity={0} _groupHover={{ opacity: 1 }}
                                                onClick={(e) => confirmarDelete(conv, e)} aria-label="Excluir" flexShrink={0}
                                            />
                                        </Tooltip>
                                    </Flex>
                                </Box>
                            );
                        })}
                    </VStack>
                )}
            </Box>
        </Flex>
    );

    // ====== AREA DE CHAT ======
    const renderChat = () => (
        <Flex direction="column" h="100%" overflow="hidden">
            {/* Header */}
            <Flex px={4} py={3} borderBottom="1px solid" borderColor="gray.200" align="center" gap={3} bg="white" flexShrink={0}>
                {isMobile && (
                    <IconButton
                        icon={conversaAtiva ? <ArrowLeft size={18} /> : <Menu size={18} />}
                        size="sm" variant="ghost"
                        onClick={() => conversaAtiva ? novaConversa() : setSidebarAberta(true)}
                        aria-label="Menu"
                    />
                )}
                <Avatar size="sm" bg="orange.400" icon={<Bot size={18} color="white" />} />
                <Box flex={1} minW={0}>
                    <Text fontWeight="bold" fontSize="sm" isTruncated>
                        {conversaAtiva ? conversaAtiva.titulo : "Nova Conversa"}
                    </Text>
                    <Text fontSize="xs" color="gray.500">Assistente IA interno</Text>
                </Box>
                {conversaAtiva && (
                    <Tooltip label="Nova conversa">
                        <IconButton icon={<Plus size={16} />} size="sm" variant="ghost" colorScheme="orange" onClick={novaConversa} aria-label="Nova conversa" />
                    </Tooltip>
                )}
            </Flex>

            {/* Mensagens */}
            <Box flex={1} overflowY="auto" px={{ base: 3, md: 5 }} py={4} bg="gray.50" style={{ minHeight: 0 }}>
                {loadingMensagens ? (
                    <Flex justify="center" align="center" h="100%">
                        <VStack><Spinner color="orange.500" size="lg" /><Text fontSize="sm" color="gray.500">Carregando mensagens...</Text></VStack>
                    </Flex>
                ) : mensagens.length === 0 && !conversaAtiva ? (
                    <Flex justify="center" align="center" h="100%">
                        <VStack spacing={4} maxW="400px" textAlign="center">
                            <Box bg="orange.50" p={4} borderRadius="full"><Sparkles size={40} color="#DD6B20" /></Box>
                            <Text fontWeight="bold" fontSize="lg" color="gray.700">Assistente de Vendas IA</Text>
                            <Text color="gray.500" fontSize="sm" lineHeight="1.6">
                                Pergunte sobre produtos, precos, estoque, codigos equivalentes ou envie fotos de pecas para identificacao.
                            </Text>
                            <VStack spacing={2} w="100%">
                                {[
                                    "Quais discos de embreagem temos para Scania 113?",
                                    "Tem cubo de roda para VW Constellation?",
                                    "Codigo equivalente ao 2RD395120?",
                                    "Vista explodida do apoio do motor do MAN TGL 8T"
                                ].map((sug, i) => (
                                    <Box
                                        key={i} w="100%" px={4} py={2.5} bg="white" borderRadius="lg"
                                        border="1px solid" borderColor="gray.200" cursor="pointer"
                                        _hover={{ borderColor: "orange.300", bg: "orange.50" }}
                                        transition="all 0.15s"
                                        onClick={() => { setInputMsg(sug); inputRef.current?.focus(); }}
                                    >
                                        <Text fontSize="sm" color="gray.600">{sug}</Text>
                                    </Box>
                                ))}
                            </VStack>
                        </VStack>
                    </Flex>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {mensagens.map((msg) => {
                            if (msg.role === "error") {
                                return (
                                    <Flex key={msg.id} justify="center">
                                        <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="lg" px={4} py={3} maxW="90%">
                                            <HStack spacing={2} mb={1}>
                                                <AlertTriangle size={14} color="#E53E3E" />
                                                <Text fontWeight="600" fontSize="xs" color="red.600">{msg.titulo}</Text>
                                            </HStack>
                                            <Text fontSize="xs" color="red.700">{msg.conteudo}</Text>
                                            {msg.podeRetentar && (
                                                <Button size="xs" colorScheme="red" variant="ghost" leftIcon={<RefreshCw size={12} />} mt={2}
                                                    onClick={() => { setMensagens(prev => prev.filter(m => m.id !== msg.id)); if (inputMsg) enviarMensagem(); }}>
                                                    Tentar novamente
                                                </Button>
                                            )}
                                        </Box>
                                    </Flex>
                                );
                            }

                            const isUser = msg.role === "user";
                            return (
                                <Flex key={msg.id} justify={isUser ? "flex-end" : "flex-start"} align="flex-start" gap={2}>
                                    {!isUser && (
                                        <Avatar size="xs" bg="orange.400" icon={<Bot size={14} color="white" />} flexShrink={0} mt={1} />
                                    )}
                                    <Box
                                        maxW={{ base: "85%", md: "75%" }}
                                        bg={isUser ? "orange.500" : "white"}
                                        color={isUser ? "white" : "gray.800"}
                                        px={4} py={2.5}
                                        borderRadius="xl"
                                        borderTopRightRadius={isUser ? "sm" : "xl"}
                                        borderTopLeftRadius={isUser ? "xl" : "sm"}
                                        boxShadow="sm"
                                        border={isUser ? "none" : "1px solid"}
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.6">
                                            {msg.conteudo}
                                        </Text>
                                        {renderAnexosMensagem(msg.anexos, isUser)}
                                        {msg.criado_em && (
                                            <Text fontSize="10px" color={isUser ? "whiteAlpha.700" : "gray.400"} mt={1} textAlign="right">
                                                {formatarData(msg.criado_em)}
                                            </Text>
                                        )}
                                    </Box>
                                    {isUser && (
                                        <Avatar size="xs" bg="gray.400" icon={<User size={14} color="white" />} flexShrink={0} mt={1} />
                                    )}
                                </Flex>
                            );
                        })}

                        {enviando && (
                            <Flex justify="flex-start" align="flex-start" gap={2}>
                                <Avatar size="xs" bg="orange.400" icon={<Bot size={14} color="white" />} flexShrink={0} mt={1} />
                                <Box bg="white" px={4} py={3} borderRadius="xl" borderTopLeftRadius="sm" boxShadow="sm" border="1px solid" borderColor="gray.100" maxW={{ base: "85%", md: "75%" }}>
                                    <HStack spacing={2} mb={1}>
                                        <Spinner size="xs" color="orange.400" />
                                        <Text fontSize="xs" fontWeight="600" color="orange.600">{getMensagemLoading()}</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.400">{formatarTempo(tempoEnvio)}</Text>
                                </Box>
                            </Flex>
                        )}

                        <div ref={mensagensEndRef} />
                    </VStack>
                )}
            </Box>

            {/* Input area */}
            <Box px={{ base: 3, md: 4 }} py={3} borderTop="1px solid" borderColor="gray.200" bg="white" flexShrink={0}>
                {enviando && (
                    <Flex align="center" gap={2} mb={2}>
                        <Spinner size="xs" color="orange.400" />
                        <Text fontSize="xs" color="orange.600" fontWeight="500">
                            {getMensagemLoading()} ({formatarTempo(tempoEnvio)})
                        </Text>
                    </Flex>
                )}

                {renderPreviewAnexos()}

                <HStack spacing={2}>
                    {/* Botoes de anexo */}
                    <HStack spacing={0}>
                        <Tooltip label={`Imagens (${imagens.length}/${LIMITES.imagensMax})`} placement="top">
                            <IconButton
                                icon={<ImagePlus size={18} />}
                                size="sm"
                                variant="ghost"
                                color={imagens.length ? "orange.500" : "gray.400"}
                                _hover={{ color: "orange.500", bg: "orange.50" }}
                                onClick={() => imagemInputRef.current?.click()}
                                isDisabled={enviando || imagens.length >= LIMITES.imagensMax}
                                aria-label="Anexar imagens"
                            />
                        </Tooltip>
                        <Tooltip label="PDF" placement="top">
                            <IconButton
                                icon={<FileText size={18} />}
                                size="sm"
                                variant="ghost"
                                color={pdf ? "red.500" : "gray.400"}
                                _hover={{ color: "red.500", bg: "red.50" }}
                                onClick={() => pdfInputRef.current?.click()}
                                isDisabled={enviando || !!pdf}
                                aria-label="Anexar PDF"
                            />
                        </Tooltip>
                        <Tooltip label="Audio" placement="top">
                            <IconButton
                                icon={<Mic size={18} />}
                                size="sm"
                                variant="ghost"
                                color={audio ? "purple.500" : "gray.400"}
                                _hover={{ color: "purple.500", bg: "purple.50" }}
                                onClick={() => audioInputRef.current?.click()}
                                isDisabled={enviando || !!audio}
                                aria-label="Anexar audio"
                            />
                        </Tooltip>
                    </HStack>

                    <Input
                        ref={inputRef}
                        placeholder={temAnexos ? "Adicione uma mensagem (opcional)..." : "Pergunte sobre produtos, precos, estoque..."}
                        value={inputMsg}
                        onChange={(e) => setInputMsg(e.target.value)}
                        onKeyDown={handleKeyDown}
                        isDisabled={enviando}
                        size="md"
                        borderRadius="xl"
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.200"
                        _focus={{ borderColor: "orange.400", bg: "white", boxShadow: "0 0 0 1px #DD6B20" }}
                        _placeholder={{ color: "gray.400" }}
                    />
                    <IconButton
                        icon={<Send size={18} />}
                        colorScheme="orange"
                        borderRadius="xl"
                        onClick={enviarMensagem}
                        isDisabled={!podeEnviar}
                        isLoading={enviando}
                        aria-label="Enviar"
                        size="md"
                    />
                </HStack>

                <Text fontSize="10px" color="gray.400" mt={1.5} textAlign="center">
                    {temAnexos
                        ? <><Paperclip size={10} style={{ display: "inline", verticalAlign: "middle" }} /> {imagens.length > 0 && `${imagens.length} imagem(ns) `}{pdf && "1 PDF "}{audio && "1 audio "} — envie com ou sem texto</>
                        : "Envie texto, imagens, PDFs ou audios para consultar o assistente"
                    }
                </Text>

                {/* Hidden file inputs */}
                <input ref={imagemInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={handleImagens} />
                <input ref={pdfInputRef} type="file" accept="application/pdf" hidden onChange={handlePdf} />
                <input ref={audioInputRef} type="file" accept="audio/*" hidden onChange={handleAudio} />
            </Box>
        </Flex>
    );

    return (
        <Box p={{ base: 2, md: 4 }} maxW="1800px" mx="auto">
            <Flex align="center" mb={3} gap={{ base: 2, md: 3 }}>
                <Bot size={22} color="#b35e20" />
                <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold">Chat IA</Text>
                <Badge colorScheme="orange" fontSize="xs">BETA</Badge>
                {isMobile && (
                    <IconButton icon={<Menu size={18} />} size="sm" variant="ghost" ml="auto" onClick={() => setSidebarAberta(true)} aria-label="Conversas" />
                )}
            </Flex>

            <Flex gap={{ base: 0, md: 3 }} h={ALTURA_TOTAL}>
                <Flex direction="column" w="280px" minW="280px" maxW="280px" bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" display={{ base: "none", lg: "flex" }} overflow="hidden">
                    {renderSidebar()}
                </Flex>

                {isMobile && (
                    <Drawer isOpen={sidebarAberta} placement="left" onClose={() => setSidebarAberta(false)}>
                        <DrawerOverlay />
                        <DrawerContent maxW="300px">
                            <DrawerCloseButton />
                            <DrawerHeader fontSize="md" borderBottom="1px solid" borderColor="gray.100" py={3}>Conversas</DrawerHeader>
                            <DrawerBody p={0}>{renderSidebar()}</DrawerBody>
                        </DrawerContent>
                    </Drawer>
                )}

                <Flex flex={1} direction="column" bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden" minW={0}>
                    {renderChat()}
                </Flex>
            </Flex>

            <AlertDialog isOpen={deleteOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4}>
                        <AlertDialogHeader fontSize="md" pb={2}>Excluir conversa</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm" color="gray.600">Tem certeza que deseja excluir esta conversa? Todas as mensagens serao perdidas.</AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button ref={cancelDeleteRef} onClick={onDeleteClose} size="sm">Cancelar</Button>
                            <Button colorScheme="red" onClick={executarDelete} size="sm">Excluir</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default ChatIA;
